/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import {
	Settings,
	Platform,
	ShoppingItem,
	SofaRangeName,
	YmalSectionType,
} from 'sofa-reborn-react';
import { CookieConsentService } from '@inter-ikea-kompis/services';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { localeService, UrlService } from '../services';
import {
	InsightsApi,
	DesignInteractionEnum,
	IpexMomentEnum,
	IItems,
	IArticleData,
	DesignSourceEnum,
	PlatformEnum,
} from '@insights/insights-data-provider';
import { IShoppingProduct } from '@inter-ikea-kompis/types';
import { AnalyticsManager } from '@inter-ikea-kompis/analytics';
import * as optimizelySDK from '@optimizely/optimizely-sdk';

let insightDataProviderDisabled = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let insightsApi: any = null;

declare const window: any;
const cookieName = 'sofas_opti';

class StatisticsService {
	private static async getCookieConsent(settings: Settings): Promise<boolean> {
		const cookieConsentService = new CookieConsentService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const consentLevel = 2;
		try {
			return await cookieConsentService.hasCookieConsent(consentLevel);
		} catch {
			return false;
		}
	}

	private static async trackingEnabled(settings: Settings, platform: Platform): Promise<boolean> {
		if (platform === 'kiosk' || !ApplicationUtils.isProduction()) {
			return true;
		}

		try {
			return await StatisticsService.getCookieConsent(settings);
		} catch (_) {
			return false;
		}
	}

	public static async sendInitPlanningSessionInsightEvent(
		code: string,
		vpcSource: DesignSourceEnum,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.initPlanningSession(code, vpcSource);
		}
	}

	public static async saveDesignInsightEvent(
		vpcCode: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		twoSeparateSofas: boolean,
		orderItems: string[] | null
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.saveDesign(vpcCode, { isSeparateSofa: twoSeparateSofas, orderItems: orderItems });
		}
	}

	public static async sendVpcInteractionEvent(
		vpcCode: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.sendDesignInteractionEvent(vpcCode, DesignInteractionEnum.storeConfiguration);
		}
	}

	public static async sendAddToBagInsightEvent(
		bagType: string,
		products: IShoppingProduct[],
		shoppingItems: ShoppingItem[],
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		fromMain: boolean,
		vpcCode: string | null,
		productType?: string | null,
		sectionType?: YmalSectionType | null,
		isIkeaPlus?: boolean,
		isCoverOnly?: boolean,
		hasAdditionalCover?: boolean,
		containsSeparateSofa?: boolean,
		orderItems?: string[] | null
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}
		const content = products[0].product.content;
		if (content) {
			const currency: string = content.priceInformation?.salesPrice[0].currencyCode || '';
			const items = shoppingItems.map((shoppingItem: ShoppingItem) => {
				const ikeaProduct = products.find((product: IShoppingProduct) => {
					return (
						product.product.content &&
						(product.product.content.itemNoGlobal === shoppingItem.id ||
							product.product.content.itemNoLocal === shoppingItem.id)
					);
				});

				const item: IArticleData = {
					id: shoppingItem.id,
					price:
						ikeaProduct &&
						ikeaProduct.product.content &&
						ikeaProduct.product.content.priceInformation
							? ikeaProduct.product.content.priceInformation.salesPrice[0].priceInclTax *
							  shoppingItem.quantity
							: 0,
					qty: shoppingItem.quantity,
				};

				return item;
			});
			const payload = fromMain
				? {
						from: 'main',
						productType: productType,
						isIkeaPlus: isIkeaPlus,
						isCoverOnly: isCoverOnly,
						hasAdditionalCover: hasAdditionalCover,
						isSeparateSofa: containsSeparateSofa,
						orderItems: orderItems ? orderItems : null,
				  }
				: { from: 'ymal', sectionType: sectionType };
			const addToBagObject: IItems = {
				currency: currency,
				items: items,
				designId: vpcCode || undefined,
				payload: payload,
			};

			if (insightsApi) {
				if (bagType === 'Cart') {
					insightsApi.addToCart(addToBagObject);
				} else if (bagType === 'List') {
					insightsApi.addToWishList(addToBagObject);
				}
			}
		}
	}

	public static async sendCustomInsightEvent(
		event: string,
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		moment: IpexMomentEnum,
		payload?: {}
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		// No consent, do not send data to analytics
		if (!trackingEnabled) {
			if (!insightDataProviderDisabled) {
				StatisticsService.disableTracking();
			}
			return;
		}

		if (insightDataProviderDisabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			insightDataProviderDisabled = false;
		}

		if (insightsApi) {
			insightsApi.sendEvent({
				ipexMoment: moment,
				event: event,
				payload: payload,
			});
		}
	}

	public static async initStatistics(
		settings: Settings,
		platform: Platform,
		range: SofaRangeName,
		callback: (optimizelyClient: any) => void
	): Promise<void> {
		const trackingEnabled = await StatisticsService.trackingEnabled(settings, platform);
		insightsApi = new InsightsApi();
		AnalyticsManager.setInsightsApi(insightsApi);
		// No consent, do not send data to analytics
		if (trackingEnabled) {
			await StatisticsService.initInsightDataProvider(platform, range);
			StatisticsService.initOptimizely(callback);
		} else {
			StatisticsService.disableTracking();
		}
	}

	private static initOptimizely(callback: (optimizelyClient: any) => void): void {
		const optimizelyClient: any = optimizelySDK.createInstance({
			sdkKey: ApplicationUtils.isProduction() ? 'W7fBdfLvnUSfasvFV9c6T' : 'XAm67fWvMuyCCcfHyBauY',
			eventBatchSize: 10,
			eventFlushInterval: 1000,
		});

		// Set log level.
		// https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript
		optimizelySDK.setLogLevel('warn');

		optimizelyClient.notificationCenter.addNotificationListener(
			optimizelySDK.enums.NOTIFICATION_TYPES.OPTIMIZELY_CONFIG_UPDATE,
			() => {
				callback(optimizelyClient);
			}
		);
	}

	private static async initInsightDataProvider(
		platform: Platform,
		rangeName: SofaRangeName
	): Promise<void> {
		// Insight analytics data provider
		let storeId = UrlService.getStoreId();
		if (storeId) {
			const firstChar = storeId.match('[a-zA-Z]');
			// Check if storeId contains a char, if it does only get the numbers before the char
			if (firstChar) {
				const index = storeId.indexOf(firstChar[0]);
				storeId = storeId.substring(0, index);
			}
		}
		if (insightsApi) {
			const kioskId =
				UrlService.getApplicationParams().kioskId ||
				UrlService.getApplicationParams().computerId ||
				undefined;
			await insightsApi.connectApplication({
				applicationId: rangeName.toUpperCase(),
				applicationVersion: '1.5',
				platform: PlatformEnum.owfe,
				countryCode: localeService.getCountryCode(),
				languageCode: localeService.getLanguageCode(),
				storeId: platform === 'kiosk' ? storeId : null,
				kioskId: kioskId,
				kiosk: platform === 'kiosk',
				dev: !ApplicationUtils.isProduction(),
				isPlanner: true,
				enableGa: true,
			});
		}
	}

	public static setABVersion(abVersionName: string, abVersionVariation: string): void {
		if (insightsApi) {
			insightsApi.setAbVersion(abVersionName, abVersionVariation);
		}
	}

	public static getOptimizelyCookie(): string | null {
		const nameEQ = cookieName + '=';
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') {
				c = c.substring(1, c.length);
			}

			if (c.indexOf(nameEQ) === 0) {
				return c.substring(nameEQ.length, c.length);
			}
		}
		return null;
	}

	public static setOptimizelyCookie(value: string): void {
		const days = 365;
		let expires = '';
		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = '; expires=' + date.toUTCString();
		}

		document.cookie = cookieName + '=' + (value || '') + expires + '; path=/';
	}

	public static eraseOptimizelyCookie(): void {
		document.cookie = cookieName + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
	}

	private static disableTracking(): void {
		if (insightsApi) {
			insightsApi.disconnect();
		}
		insightDataProviderDisabled = true;

		StatisticsService.eraseOptimizelyCookie();
		if (window.optimizely) {
			window.optimizely.push({
				type: 'disable',
			});

			window.optimizely.push({
				type: 'optOut',
				isOptOut: true,
			});
		}
	}

	public static generateOptimizelyUserId(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}
}

export default StatisticsService;
